/**
 * Inference Engine
 * Handles image preprocessing, inference execution, and postprocessing.
 * AI: depth estimation (ONNX). Three.js/WebGL: relighting + post (normals, SSAO, bloom, tone map).
 */

import { loadModel, getInputShape } from './modelLoader';
import * as ort from 'onnxruntime-web';

/**
 * Preprocess image for ONNX model input
 * @param {HTMLImageElement|HTMLCanvasElement} imageElement
 * @param {number[]} targetShape - [batch, channels, height, width]
 * @returns {Float32Array} Preprocessed tensor data
 */
export function preprocessImage(imageElement, targetShape = [1, 3, 384, 384]) {
  const [batch, channels, height, width] = targetShape;
  
  // Create canvas for resizing
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  // Draw and resize image
  ctx.drawImage(imageElement, 0, 0, width, height);
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Convert to Float32Array and normalize to [0, 1]
  const tensorData = new Float32Array(batch * channels * height * width);
  
  for (let i = 0; i < height * width; i++) {
    const r = data[i * 4] / 255.0;
    const g = data[i * 4 + 1] / 255.0;
    const b = data[i * 4 + 2] / 255.0;
    
    // ONNX models typically expect CHW format
    tensorData[i] = r;                    // R channel
    tensorData[height * width + i] = g;   // G channel
    tensorData[2 * height * width + i] = b; // B channel
  }
  
  return tensorData;
}

/**
 * Run inference on preprocessed image
 * @param {ort.InferenceSession} session
 * @param {Float32Array} inputTensor
 * @param {number[]} inputShape
 * @returns {Promise<ort.Tensor>} Output tensor
 */
export async function runInference(session, inputTensor, inputShape) {
  try {
    if (!session || !session.inputNames || session.inputNames.length === 0) {
      throw new Error('Session has no input names');
    }
    
    const inputName = session.inputNames[0];
    
    // Create ONNX tensor
    const tensor = new ort.Tensor('float32', inputTensor, inputShape);
    
    // Run inference
    const feeds = { [inputName]: tensor };
    const results = await session.run(feeds);
    
    // Get first output
    if (!session.outputNames || session.outputNames.length === 0) {
      throw new Error('Session has no output names');
    }
    
    console.log('Session output names:', session.outputNames);
    console.log('Inference results keys:', Object.keys(results));
    
    const outputName = session.outputNames[0];
    const outputTensor = results[outputName];
    
    if (!outputTensor) {
      console.error('Available outputs:', Object.keys(results));
      console.error('Expected output name:', outputName);
      throw new Error(`Output "${outputName}" not found in inference results. Available: ${Object.keys(results).join(', ')}`);
    }
    
    console.log('Output tensor type:', typeof outputTensor);
    console.log('Output tensor:', outputTensor);
    console.log('Output tensor dims:', outputTensor.dims);
    console.log('Output tensor data type:', typeof outputTensor.data);
    console.log('Output tensor data length:', outputTensor.data?.length);
    
    // Clarify dataLocation - this is normal!
    if (outputTensor.dataLocation) {
      console.log(`ℹ️  Output tensor dataLocation: ${outputTensor.dataLocation}`);
      if (outputTensor.dataLocation === 'cpu') {
        console.log('✅ This is NORMAL - GPU computation completed, data transferred to CPU for JavaScript access');
        console.log('✅ The inference DID run on GPU, results are just copied to CPU memory');
      }
    }
    
    if (!outputTensor.dims) {
      console.error('Output tensor structure:', outputTensor);
      console.error('Output tensor keys:', Object.keys(outputTensor));
      throw new Error('Output tensor does not have dims property');
    }
    
    return outputTensor;
  } catch (error) {
    console.error('Inference error:', error);
    throw new Error(`Inference failed: ${error.message}`);
  }
}

/**
 * Postprocess depth output: build depth map canvas only (grayscale at original resolution).
 * Relighting and preview are done by runRelightPipeline (Three.js/WebGL).
 * @param {ort.Tensor} outputTensor
 * @param {HTMLImageElement} originalImage
 * @param {number} originalWidth
 * @param {number} originalHeight
 * @returns {{ depthCanvas: HTMLCanvasElement }} Depth map canvas for display and for relight pipeline
 */
export function postprocessDepth(outputTensor, originalImage, originalWidth, originalHeight) {
  if (!outputTensor) {
    throw new Error('Output tensor is undefined');
  }
  
  if (!outputTensor.data) {
    throw new Error('Output tensor has no data property');
  }
  
  if (!outputTensor.dims) {
    console.error('Output tensor structure:', outputTensor);
    throw new Error('Output tensor has no dims property');
  }
  
  const outputData = outputTensor.data;
  const outputDims = outputTensor.dims;
  
  // Handle different output shapes
  let depthData;
  let depthWidth, depthHeight;
  
  if (outputDims.length === 4) {
    // [batch, channels, height, width] or [batch, height, width, channels]
    if (outputDims[1] === 1) {
      // [batch, 1, height, width]
      depthHeight = outputDims[2];
      depthWidth = outputDims[3];
      depthData = new Float32Array(depthWidth * depthHeight);
      for (let i = 0; i < depthWidth * depthHeight; i++) {
        depthData[i] = outputData[i];
      }
    } else {
      // Assume [batch, height, width, channels]
      depthHeight = outputDims[1];
      depthWidth = outputDims[2];
      depthData = new Float32Array(depthWidth * depthHeight);
      for (let i = 0; i < depthWidth * depthHeight; i++) {
        depthData[i] = outputData[i * outputDims[3]]; // Take first channel
      }
    }
  } else if (outputDims.length === 3) {
    // Could be [batch, height, width], [channels, height, width], or [height, width, channels]
    if (outputDims[0] === 1) {
      // [1, height, width] - batch dimension is 1, skip it
      depthHeight = outputDims[1];
      depthWidth = outputDims[2];
      depthData = new Float32Array(depthWidth * depthHeight);
      // Skip the batch dimension (first element)
      for (let i = 0; i < depthWidth * depthHeight; i++) {
        depthData[i] = outputData[i];
      }
    } else if (outputDims[2] === 1 || outputDims[2] <= 4) {
      // Likely [height, width, channels] where channels is small
      depthHeight = outputDims[0];
      depthWidth = outputDims[1];
      const channels = outputDims[2];
      depthData = new Float32Array(depthWidth * depthHeight);
      // Take first channel or average channels
      for (let i = 0; i < depthWidth * depthHeight; i++) {
        depthData[i] = outputData[i * channels]; // Take first channel
      }
    } else {
      // Assume [channels, height, width] where first dim is channels
      depthHeight = outputDims[1];
      depthWidth = outputDims[2];
      depthData = new Float32Array(depthWidth * depthHeight);
      // Take first channel
      const channelSize = depthWidth * depthHeight;
      for (let i = 0; i < channelSize; i++) {
        depthData[i] = outputData[i];
      }
    }
  } else if (outputDims.length === 2) {
    // [height, width]
    depthHeight = outputDims[0];
    depthWidth = outputDims[1];
    depthData = Array.from(outputData);
  } else {
    // Flatten and assume square
    const size = Math.sqrt(outputData.length);
    depthHeight = depthWidth = Math.floor(size);
    depthData = Array.from(outputData);
  }
  
  // Normalize depth values - use loop to avoid stack overflow with large arrays
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let validPixels = 0;
  
  for (let i = 0; i < depthData.length; i++) {
    const value = depthData[i];
    if (isFinite(value) && !isNaN(value)) {
      if (value < min) min = value;
      if (value > max) max = value;
      sum += value;
      validPixels++;
    }
  }
  
  const range = max - min || 1;
  const mean = validPixels > 0 ? sum / validPixels : 0;
  
  // Diagnostic logging to verify model is working
  console.log('📊 Depth Data Analysis:');
  console.log(`   - Depth map size: ${depthWidth} x ${depthHeight} = ${depthData.length} pixels`);
  console.log(`   - Valid pixels: ${validPixels} / ${depthData.length}`);
  console.log(`   - Depth range: [${min.toFixed(4)}, ${max.toFixed(4)}]`);
  console.log(`   - Depth mean: ${mean.toFixed(4)}`);
  console.log(`   - Depth std dev: ${Math.sqrt(depthData.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / validPixels).toFixed(4)}`);
  
  // Check if depth data is valid
  if (validPixels === 0) {
    console.error('❌ ERROR: No valid depth data found! Model may not be working correctly.');
    throw new Error('Invalid depth data: all values are NaN or Infinity');
  }
  
  if (range < 0.001) {
    console.warn('⚠️  WARNING: Depth range is very small. Model may not be producing meaningful depth.');
  } else {
    console.log('✅ Depth data appears valid - model is working correctly!');
  }
  
  // Create depth map visualization (grayscale: closer = brighter, farther = darker)
  const depthCanvas = document.createElement('canvas');
  depthCanvas.width = depthWidth;
  depthCanvas.height = depthHeight;
  const depthCtx = depthCanvas.getContext('2d');
  const depthImageData = depthCtx.createImageData(depthWidth, depthHeight);
  for (let i = 0; i < depthData.length; i++) {
    const normalized = (depthData[i] - min) / range;
    const gray = Math.floor(normalized * 255);
    const idx = i * 4;
    depthImageData.data[idx] = gray;
    depthImageData.data[idx + 1] = gray;
    depthImageData.data[idx + 2] = gray;
    depthImageData.data[idx + 3] = 255;
  }
  depthCtx.putImageData(depthImageData, 0, 0);

  // Scale depth map to original image dimensions so it matches the preview (not cropped)
  const depthDisplayCanvas = document.createElement('canvas');
  depthDisplayCanvas.width = originalWidth;
  depthDisplayCanvas.height = originalHeight;
  const depthDisplayCtx = depthDisplayCanvas.getContext('2d');
  depthDisplayCtx.drawImage(depthCanvas, 0, 0, depthWidth, depthHeight, 0, 0, originalWidth, originalHeight);

  return { depthCanvas: depthDisplayCanvas };
}

/**
 * Complete inference pipeline
 * @param {string} modelPath
 * @param {HTMLImageElement} imageElement
 * @param {string} device
 * @returns {Promise<HTMLCanvasElement>} Preview canvas
 */
export async function processImage(modelPath, imageElement, device = 'wasm') {
  const startTime = performance.now();
  
  try {
    // Load model
    console.log('Step 1: Loading model...');
    const modelLoadStartTime = performance.now();
    const session = await loadModel(modelPath, device);
    const modelLoadEndTime = performance.now();
    const modelLoadTime = modelLoadEndTime - modelLoadStartTime;
    console.log(`⏱️  Model loading time: ${modelLoadTime.toFixed(2)}ms`);
    
    if (!session) {
      throw new Error('Failed to create inference session');
    }
    
    console.log('Step 2: Getting input shape...');
    const inputShapeStartTime = performance.now();
    const inputShape = getInputShape(session);
    const inputShapeEndTime = performance.now();
    const inputShapeTime = inputShapeEndTime - inputShapeStartTime;
    console.log('Input shape:', inputShape);
    console.log(`⏱️  Input shape extraction time: ${inputShapeTime.toFixed(2)}ms`);
    
    if (!inputShape || !Array.isArray(inputShape) || inputShape.length === 0) {
      throw new Error(`Invalid input shape: ${inputShape}`);
    }
    
    // Preprocess
    console.log('Step 3: Preprocessing image...');
    const preprocessStartTime = performance.now();
    const inputTensor = preprocessImage(imageElement, inputShape);
    const preprocessEndTime = performance.now();
    const preprocessTime = preprocessEndTime - preprocessStartTime;
    console.log('Input tensor size:', inputTensor.length);
    console.log(`⏱️  Preprocessing time: ${preprocessTime.toFixed(2)}ms`);
    
    // Run inference
    console.log('Step 4: Running inference...');
    console.log(`Device requested: ${device.toUpperCase()}`);
    
    if (device === 'webgpu') {
      console.log('🔍 Executing inference on GPU (WebGPU)...');
      console.log('ℹ️  WASM stack traces in console are initialization only, not actual execution');
    } else {
      console.log('🔍 Executing inference on CPU (WASM)...');
    }
    
    const inferenceStartTime = performance.now();
    
    // Monitor for WebGPU errors during inference
    let webgpuErrors = [];
    const originalConsoleError = console.error;
    const errorListener = (event) => {
      if (event.message && event.message.includes('WebGPU validation error')) {
        webgpuErrors.push(event.message);
      }
    };
    
    // Try to catch WebGPU errors (they may still be uncaught, but we'll log them)
    let outputTensor;
    try {
      outputTensor = await runInference(session, inputTensor, inputShape);
    } catch (inferenceError) {
      console.error('❌ Inference failed:', inferenceError);
      if (device === 'webgpu') {
        console.error('⚠️  WebGPU inference failed. This may be due to f16 validation errors.');
        console.error('💡 Suggestion: Try using a non-f16 model or fallback to WASM');
      }
      throw inferenceError;
    }
    
    const inferenceEndTime = performance.now();
    const inferenceTime = inferenceEndTime - inferenceStartTime;
    
    console.log(`⏱️ Inference execution time: ${inferenceTime.toFixed(2)}ms`);
    
    // Check if inference completed despite WebGPU errors
    if (device === 'webgpu' && webgpuErrors.length > 0) {
      console.warn(`⚠️  Detected ${webgpuErrors.length} WebGPU validation errors during inference`);
      console.warn('⚠️  However, inference completed - results may still be valid');
      console.warn('💡 For better compatibility, consider using a non-f16 model');
    } else if (device === 'webgpu') {
      console.log('✅ No WebGPU errors detected during inference');
    }
    
    // Performance hint: GPU inference is typically much faster than CPU
    if (device === 'webgpu') {
      if (inferenceTime < 2000) {
        console.log('✅ Fast inference time suggests GPU acceleration is ACTIVE');
        console.log('✅ You are using GPU for inference');
      } else {
        console.log('⚠️ Slower inference time - checking if GPU is actually being used...');
        console.log('⚠️ If inference is slow, WebGPU may have silently fallen back to CPU');
      }
    } else {
      console.log('ℹ️  CPU inference (WASM) - this is expected for WASM execution provider');
    }
    
    if (!outputTensor) {
      throw new Error('Inference returned no output tensor');
    }
    
    console.log('Step 5: Depth map + relighting (Three.js WebGPU/TSL→WGSL or WebGL fallback)...');
    const postprocessStartTime = performance.now();
    
    // AI output → depth map canvas only
    const { depthCanvas } = postprocessDepth(
      outputTensor,
      imageElement,
      imageElement.width,
      imageElement.height
    );
    
    // Load only one relight pipeline to avoid "Multiple instances of Three.js".
    // useWebGPUPipeline true → load three/webgpu (WebGPU). false → load three (WebGL).
    const useWebGPUPipeline = true; // set false for WebGL-only (single Three.js instance)
    let previewCanvas;
    if (useWebGPUPipeline) {
      try {
        const { runRelightPipelineWebGPU } = await import('./relightPipelineWebGPU.js');
        previewCanvas = await runRelightPipelineWebGPU(
          imageElement,
          depthCanvas,
          imageElement.width,
          imageElement.height,
          { dofEnabled: false, lutIntensity: 0.35, sharpeningStrength: 0.15 }
        );
        console.log('✅ Relight pipeline: WebGPU + WGSL (color grading, sharpening, DOF)');
      } catch (webgpuErr) {
        console.warn('WebGPU relight failed, falling back to WebGL:', webgpuErr?.message || webgpuErr);
        const { runRelightPipeline } = await import('./relightPipeline.js');
        previewCanvas = runRelightPipeline(
          imageElement,
          depthCanvas,
          imageElement.width,
          imageElement.height
        );
      }
    } else {
      const { runRelightPipeline } = await import('./relightPipeline.js');
      previewCanvas = runRelightPipeline(
        imageElement,
        depthCanvas,
        imageElement.width,
        imageElement.height
      );
    }
    
    const postprocessEndTime = performance.now();
    const postprocessTime = postprocessEndTime - postprocessStartTime;
    
    const endTime = performance.now();
    const executionTime = endTime - startTime;
    
    console.log(`⏱️  Postprocessing time: ${postprocessTime.toFixed(2)}ms (CPU-bound)`);
    console.log(`⏱️  Total pipeline time: ${executionTime.toFixed(2)}ms`);
    console.log(`📊 Detailed Breakdown:`);
    console.log(`   - Model Loading: ${modelLoadTime.toFixed(2)}ms (${((modelLoadTime / executionTime) * 100).toFixed(1)}%)`);
    console.log(`   - Input Shape: ${inputShapeTime.toFixed(2)}ms (${((inputShapeTime / executionTime) * 100).toFixed(1)}%)`);
    console.log(`   - Preprocessing: ${preprocessTime.toFixed(2)}ms (${((preprocessTime / executionTime) * 100).toFixed(1)}%)`);
    console.log(`   - GPU Inference: ${inferenceTime.toFixed(2)}ms (${((inferenceTime / executionTime) * 100).toFixed(1)}%)`);
    console.log(`   - CPU Postprocessing: ${postprocessTime.toFixed(2)}ms (${((postprocessTime / executionTime) * 100).toFixed(1)}%)`);
    
    const overheadTime = executionTime - modelLoadTime - inputShapeTime - preprocessTime - inferenceTime - postprocessTime;
    if (overheadTime > 10) {
      console.log(`   - Overhead/Other: ${overheadTime.toFixed(2)}ms (${((overheadTime / executionTime) * 100).toFixed(1)}%)`);
    }
    
    // Performance summary
    console.log(`📈 Performance Summary:`);
    console.log(`   ✅ GPU Inference: ${inferenceTime.toFixed(2)}ms - Excellent!`);
    if (modelLoadTime > 1000) {
      console.log(`   ⏳ Model Loading: ${modelLoadTime.toFixed(2)}ms - First-time load (will be cached)`);
      console.log(`   💡 Next run will use cached model (< 10ms)`);
    } else {
      console.log(`   ✅ Model Loading: ${modelLoadTime.toFixed(2)}ms - Using cached model!`);
    }
    if (preprocessTime > 1000) {
      console.log(`   ⚠️  Preprocessing: ${preprocessTime.toFixed(2)}ms - May be slow for large images`);
    } else {
      console.log(`   ✅ Preprocessing: ${preprocessTime.toFixed(2)}ms - Good!`);
    }
    
    return { 
      canvas: previewCanvas, 
      depthCanvas,
      executionTime,
      inferenceTime,
      postprocessTime,
      modelLoadTime,
      preprocessTime
    };
  } catch (error) {
    console.error('Error in processImage:', error);
    console.error('Error stack:', error.stack);
    throw error;
  }
}
